// The settings cards every product has: interface language, billing, team.
// A product's Settings page composes these with its own cards.

import { LOCALES, type Locale } from '@quoter/shared/locale'
import { useI18n } from '../i18n/LocaleContext'
import type { CoreStrings } from '../i18n/index'
import { useMembers, memberLabel, initials } from '../hooks/useMembers'
import { useBillingPortal, useSubscription, type SubscriptionView } from '../hooks/useBilling'
import { Button } from '../ui/Button'
import { Card, CardTitle, CardSubtitle } from '../ui/Card'
import { Select } from '../ui/Input'
import { Field } from '../ui/Field'
import { Badge, type BadgeTone } from '../ui/Badge'
import { LoadingBlock } from '../ui/Feedback'

/** Interface language, per user. */
export function LanguageCard() {
  const { t, locale, setLocale } = useI18n()
  return (
    <Card>
      <CardTitle>{t.language.title}</CardTitle>
      <CardSubtitle>{t.language.hint}</CardSubtitle>
      <div className="mt-5 grid gap-4 sm:grid-cols-3">
        <Field id="s-locale" label={t.language.switch}>
          <Select id="s-locale" value={locale} onChange={(e) => setLocale(e.target.value as Locale)}>
            {LOCALES.map((code) => (
              <option key={code} value={code}>
                {t.language[code]}
              </option>
            ))}
          </Select>
        </Field>
      </div>
    </Card>
  )
}

/** Everyone with access to the workspace. Read-only: invites are not self-serve yet. */
export function TeamCard() {
  const { t } = useI18n()
  const { members } = useMembers()
  return (
    <Card>
      <CardTitle>{t.settings.team}</CardTitle>
      <CardSubtitle>{t.settings.teamHint}</CardSubtitle>
      <ul className="mt-4 divide-y divide-gray-100">
        {members.map((member) => {
          const label = memberLabel(member)
          return (
            <li key={member.id} className="flex items-center gap-3 py-2.5">
              <span className="flex h-8 w-8 items-center justify-center rounded-full bg-sage-700 font-display text-[11px] text-sage-50">
                {initials(label)}
              </span>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-[13px] font-medium text-gray-900">{label}</span>
                <span className="block truncate text-[12px] text-gray-500">{member.email}</span>
              </span>
              <Badge tone={member.role === 'admin' ? 'brand' : 'neutral'}>
                {member.role === 'admin' ? t.settings.roleAdmin : t.settings.roleMember}
              </Badge>
            </li>
          )
        })}
      </ul>
      <p className="mt-4 text-[12px] text-gray-400">{t.settings.inviteHint}</p>
    </Card>
  )
}

const STATUS_TONE: Record<string, BadgeTone> = {
  active: 'success',
  trialing: 'brand',
  past_due: 'warning',
  unpaid: 'danger',
  canceled: 'neutral',
  incomplete: 'warning',
}

/** Which date matters, and what to call it, for one subscription state. */
function billingDateLine(
  sub: SubscriptionView,
  t: CoreStrings,
  formatDate: (value: string | null | undefined) => string,
): { label: string; value: string } | null {
  if (sub.status === 'trialing' && sub.trial_end) {
    return { label: t.settings.billingTrialEnds, value: formatDate(sub.trial_end) }
  }
  if (!sub.current_period_end) return null
  if (sub.cancel_at_period_end || sub.status === 'canceled') {
    return { label: t.settings.billingEnds, value: formatDate(sub.current_period_end) }
  }
  return { label: t.settings.billingRenews, value: formatDate(sub.current_period_end) }
}

/** Plan, status and the way into the Stripe portal. Render for admins only. */
export function BillingCard() {
  const { t, formatDate } = useI18n()
  const { subscription, loading } = useSubscription()
  const { openPortal, opening, error } = useBillingPortal()

  const dateLine = subscription ? billingDateLine(subscription, t, formatDate) : null
  const statusLabel = subscription
    ? ((t.settings.subscriptionStatus as Record<string, string>)[subscription.status] ??
      t.settings.subscriptionStatus.unknown)
    : null

  return (
    <Card>
      <CardTitle>{t.settings.billing}</CardTitle>
      <CardSubtitle>{t.settings.billingHint}</CardSubtitle>

      {loading ? (
        <div className="mt-4">
          <LoadingBlock />
        </div>
      ) : !subscription ? (
        <p className="mt-4 text-[13px] text-gray-500">{t.settings.billingNone}</p>
      ) : (
        <dl className="mt-4 grid gap-x-6 gap-y-3 text-[13px] sm:grid-cols-3">
          <div>
            <dt className="text-[11px] font-bold uppercase tracking-[0.1em] text-gray-500">
              {t.settings.billingPlan}
            </dt>
            <dd className="mt-1 font-medium text-gray-900">{subscription.planName ?? '—'}</dd>
          </div>
          <div>
            <dt className="text-[11px] font-bold uppercase tracking-[0.1em] text-gray-500">
              {t.settings.billingStatus}
            </dt>
            <dd className="mt-1 flex flex-wrap items-center gap-2">
              <Badge tone={STATUS_TONE[subscription.status] ?? 'neutral'}>{statusLabel}</Badge>
              {subscription.cancel_at_period_end && subscription.status !== 'canceled' && (
                <Badge tone="warning">{t.settings.billingCancelScheduled}</Badge>
              )}
            </dd>
          </div>
          {dateLine && (
            <div>
              <dt className="text-[11px] font-bold uppercase tracking-[0.1em] text-gray-500">
                {dateLine.label}
              </dt>
              <dd className="mt-1 text-gray-900">{dateLine.value}</dd>
            </div>
          )}
        </dl>
      )}

      <div className="mt-5 flex items-center gap-3">
        <Button variant="secondary" onClick={() => void openPortal('/settings')} disabled={opening}>
          {opening ? t.settings.openingPortal : t.settings.manageBilling}
        </Button>
        {error && (
          <span className="text-[12.5px] text-red-700" role="alert">
            {t.settings.portalError}
          </span>
        )}
      </div>
    </Card>
  )
}
